import classNames from 'classnames';
import {
  ErrorMessage,
  Checkbox,
  TitledBlock,
  TxtTitle,
  View,
  ContentBlock,
  PartialWidthContainer,
  FlexContainer,
  Input,
  SelectButtons,
  Spinner,
  Button,
  Txt,
  SupportBlock,
  InputImages,
  InputAddress,
  Icon,
  SelectPropertyOwnership,
} from 'components';
import { termsAndConditionsLink } from 'config';
import {
  bedroomOptions,
  featuresOptions,
  furnishingOptions,
  outsideSpaceOptions,
  parkingOptions,
  typeOptions,
} from 'config/propertyFields';
import { formatCurrency } from 'helpers/formatNumber';
import { useNavigationCallback } from 'hooks';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { propertyDefault } from 'config/dataDefaults';
import { useProperty, useDataActions } from 'state';
import { DeleteProperty } from './components';
import PropertyAddEditState from './PropertyAddEditState';
import styles from './PropertiesAddEditScreen.module.scss';

/**
 * PropertiesAddEditScreen
 *
 * @return {JSX.Element}
 */
const PropertiesAddEditScreen = ({ className }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);
  const [error, setError] = useState('');
  const dataActions = useDataActions();
  const propertyDoc = useProperty();
  const isEdit = !!propertyDoc;
  const state = useMemo(
    () => new PropertyAddEditState(propertyDoc?.data || propertyDefault()),
    [propertyDoc]
  );

  const { callbackParam } = useNavigationCallback();

  const handleChange = ({ target: { name, value } }) => {
    setError('');
    state.setState(name, value);
  };

  const closeMe = useCallback(
    () => navigate(callbackParam || '/properties', { replace: true }),
    [callbackParam, navigate]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await (isEdit
        ? dataActions.property.editProperty(propertyDoc, state.state)
        : dataActions.property.createProperty(state.state));
      closeMe();
    } catch (e) {
      setIsLoading(false);
      setError(e.message);
    }
  };

  const handleDelete = async () => {
    setIsLoadingDelete(true);
    try {
      await dataActions.property.deleteProperty(propertyDoc);
      navigate('/properties', { replace: true });
    } catch (e) {
      setIsLoadingDelete(false);
      setError(e.message);
    }
  };

  const { state: dataState } = state;

  const isDisabled = isLoading || isLoadingDelete;

  return (
    <PartialWidthContainer
      className={classNames(
        styles.PropertiesAddEditScreen,
        className,
        `${PropertiesAddEditScreen.displayName}`
      )}
      gap
      small
    >
      <Helmet title="Add Property" />
      <FlexContainer>
        <Button onClick={closeMe}>
          <Icon s name="goback" /> Back
        </Button>
      </FlexContainer>
      <ContentBlock column>
        <View>
          <TxtTitle xxxx>Enter the details of your property below</TxtTitle>
        </View>
        <View>
          <SupportBlock />
        </View>
      </ContentBlock>
      <FlexContainer column>
        <form onSubmit={handleSubmit}>
          <TitledBlock title="Postcode">
            <InputAddress
              name="address"
              value={dataState.address}
              onChange={handleChange}
              fullWidth
              disabled={state.isEdit || isDisabled}
              autoFocus
              required
              useButton
            />
          </TitledBlock>
          <TitledBlock title="Bedrooms">
            <SelectButtons
              options={bedroomOptions}
              onChange={handleChange}
              value={dataState.bedroomCount || ''}
              name="bedroomCount"
              disabled={isDisabled}
              required
            />
          </TitledBlock>
          <TitledBlock title="Bathrooms">
            <SelectButtons
              options={bedroomOptions}
              onChange={handleChange}
              value={dataState.bathroomCount || ''}
              name="bathroomCount"
              disabled={isDisabled}
              required
            />
          </TitledBlock>
          <TitledBlock title="Type">
            <SelectButtons
              options={typeOptions}
              onChange={handleChange}
              value={dataState.propertyType || ''}
              name="propertyType"
              disabled={isDisabled}
              required
            />
          </TitledBlock>
          <TitledBlock title="Furnishing">
            <SelectButtons
              options={furnishingOptions}
              onChange={handleChange}
              value={dataState.propertyFurnishing || ''}
              name="propertyFurnishing"
              disabled={isDisabled}
              required
            />
          </TitledBlock>
          <TitledBlock title="Features">
            <SelectButtons
              options={featuresOptions}
              onChange={handleChange}
              value={dataState.propertyFeatures || []}
              name="propertyFeatures"
              disabled={isDisabled}
              multiple
            />
          </TitledBlock>
          <TitledBlock title="Outside space">
            <SelectButtons
              options={outsideSpaceOptions}
              onChange={handleChange}
              value={dataState.propertyOutsideSpaces || []}
              name="propertyOutsideSpaces"
              multiple
              disabled={isDisabled}
            />
          </TitledBlock>
          <TitledBlock title="Parking">
            <SelectButtons
              options={parkingOptions}
              onChange={handleChange}
              value={dataState.propertyParking || ''}
              name="propertyParking"
              disabled={isDisabled}
              required
            />
          </TitledBlock>
          <TitledBlock title="Rent per month">
            <Input
              className={styles.InputNumber}
              pre="£"
              fullWidth
              type="number"
              min="100"
              max="50000"
              step="0.01"
              required
              name="tenancyRentAmount"
              value={dataState.tenancyRentAmount || ''}
              onChange={handleChange}
              disabled={isDisabled}
            />
          </TitledBlock>
          <TitledBlock title="Deposit">
            <Input
              className={styles.InputNumber}
              pre="£"
              fullWidth
              type="number"
              min="100"
              max={state.maxDepositAmountStr}
              step="0.01"
              name="tenancyDepositAmount"
              required
              placeholder={`Maximum deposit £${formatCurrency(
                state.maxDepositAmountStr
              )}`}
              value={dataState.tenancyDepositAmount || ''}
              onChange={handleChange}
              disabled={isDisabled}
            />
          </TitledBlock>
          <TitledBlock title="Property Ownership">
            <SelectPropertyOwnership
              name="ownership.type"
              value={dataState.ownership.type || ''}
              onChange={handleChange}
            />
          </TitledBlock>
          <TitledBlock title="Images of the property">
            <InputImages
              name="imageUrls"
              value={dataState.imageUrls}
              onChange={handleChange}
              disabled={isDisabled}
              required
              multiple
            />
          </TitledBlock>
          <FlexContainer column gap>
            {state.isEdit ? null : (
              <>
                <Checkbox
                  rounded
                  onSurface
                  required
                  name="entitledToRent"
                  disabled={isDisabled}
                >
                  I am entitled to rent this property to tenants
                </Checkbox>
                <Checkbox
                  rounded
                  onSurface
                  required
                  name="noCharge"
                  disabled={isDisabled}
                >
                  I will not charge tenants an admin fee
                </Checkbox>
                <Checkbox
                  rounded
                  onSurface
                  required
                  name="agreeTerms"
                  disabled={isDisabled}
                >
                  I have read and agree to Propa's{' '}
                  <a
                    href={termsAndConditionsLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Terms and conditions
                  </a>
                </Checkbox>
              </>
            )}
            {error ? <ErrorMessage small description={error} /> : null}
            <FlexContainer>
              <Button flex type="submit" big success disabled={isDisabled}>
                {isLoading ? (
                  <Spinner />
                ) : (
                  <FlexContainer spaceBetween alignCenter flex>
                    <Txt xx>{isEdit ? 'Update' : 'Add'} property</Txt>
                    <Txt>🎉</Txt>
                  </FlexContainer>
                )}
              </Button>
            </FlexContainer>
          </FlexContainer>
        </form>
      </FlexContainer>
      {isEdit ? (
        <DeleteProperty
          onDelete={handleDelete}
          disabled={isDisabled}
          isLoading={isLoadingDelete}
        />
      ) : null}
    </PartialWidthContainer>
  );
};

PropertiesAddEditScreen.displayName = 'PropertiesAddEditScreen';

export default observer(PropertiesAddEditScreen);
